import { createContext, type ReactNode, useContext } from 'react';

import { type AgentApprovalMode } from '@libraries/typescript/api_client';

export type AgentApprovalModeOption = {
    readonly label: string;
    readonly description: string;
};

export const AGENT_APPROVAL_MODES: ReadonlyArray<AgentApprovalMode> = [
    'review',
    'autopilot',
];

export const AGENT_APPROVAL_MODE_OPTIONS: Record<
    AgentApprovalMode,
    AgentApprovalModeOption
> = {
    review: {
        label: 'Review actions',
        description:
            'Phoebe asks before texting, calling, or changing settings',
    },
    autopilot: {
        label: 'Autopilot',
        description: 'Phoebe acts without asking for approval',
    },
};

export type AgentApprovalModeControlProps = {
    readonly pinnedMode: AgentApprovalMode | null;
    readonly resolvedMode: AgentApprovalMode;
    readonly canEdit: boolean;
    readonly disabled?: boolean;
    readonly onModeChange: (mode: AgentApprovalMode) => void;
};

type AgentApprovalModeControlRenderer = (
    props: AgentApprovalModeControlProps,
) => ReactNode;

// The legacy chat has no approval-mode control; only the native surface
// provides a renderer, so this stays a v3-owned control without adding a v3
// import or branch to the legacy chat view.
const AgentApprovalModeControlRendererContext =
    createContext<AgentApprovalModeControlRenderer | null>(null);

export function AgentApprovalModeControlRendererProvider({
    children,
    render,
}: {
    readonly children: ReactNode;
    readonly render: AgentApprovalModeControlRenderer;
}) {
    return (
        <AgentApprovalModeControlRendererContext.Provider value={render}>
            {children}
        </AgentApprovalModeControlRendererContext.Provider>
    );
}

export function AgentApprovalModeControl(props: AgentApprovalModeControlProps) {
    const renderOverride = useContext(AgentApprovalModeControlRendererContext);
    if (renderOverride === null) {
        return null;
    }
    return renderOverride(props);
}
